/**
 * Canonical chargeback workflow statuses returned by the API.
 * Values are PascalCase / snake_case segments to match backend serialization.
 */
export enum ChargebackStatus {
  /** Legacy — merchant has not responded */
  Untreated = 'Untreated',
  /** Merchant accepted */
  Accepted = 'Accepted',
  /** Merchant rejected */
  Declined = 'Declined',
  /** Merchant failed to respond within deadline */
  AutoAccepted = 'AutoAccepted',
  /** Case submitted (new flow) */
  Submitted = 'Submitted',
  /** Officer is reviewing */
  UnderReview = 'UnderReview',
  /** Merchant accepted, pending officer review */
  MerchantAccepted = 'MerchantAccepted',
  /** Merchant declined, pending officer review */
  MerchantDeclined = 'MerchantDeclined',
  /** Approved and refund paid out */
  Resolved_Refunded = 'Resolved_Refunded',
  /** Officer declined */
  Resolved_Declined = 'Resolved_Declined',
  /** Refund payout in progress */
  PayoutProcessing = 'PayoutProcessing',
  /** Held funds released back to merchant */
  Released = 'Released',
}

const STATUS_VALUES = new Set<string>(Object.values(ChargebackStatus));

/**
 * Backend may serialize {@link ChargebackStatus} as 0-based integers in this order.
 * Keep in sync with the API contract.
 */
export const CHARGEBACK_STATUS_BY_ORDINAL: readonly ChargebackStatus[] = [
  ChargebackStatus.Untreated,
  ChargebackStatus.Accepted,
  ChargebackStatus.Declined,
  ChargebackStatus.AutoAccepted,
  ChargebackStatus.Submitted,
  ChargebackStatus.UnderReview,
  ChargebackStatus.MerchantAccepted,
  ChargebackStatus.MerchantDeclined,
  ChargebackStatus.Resolved_Refunded,
  ChargebackStatus.Resolved_Declined,
  ChargebackStatus.PayoutProcessing,
  ChargebackStatus.Released,
] as const;

/** Resolve API numeric status code to {@link ChargebackStatus}, or null if out of range. */
export function chargebackStatusFromNumeric(
  raw: unknown,
): ChargebackStatus | null {
  if (typeof raw !== 'number' || !Number.isInteger(raw)) {
    return null;
  }
  if (raw < 0 || raw >= CHARGEBACK_STATUS_BY_ORDINAL.length) {
    return null;
  }
  return CHARGEBACK_STATUS_BY_ORDINAL[raw];
}

/** Short labels for UI (aligned with enum semantics). */
export const CHARGEBACK_STATUS_LABELS: Record<ChargebackStatus, string> = {
  [ChargebackStatus.Untreated]: 'Untreated',
  [ChargebackStatus.Accepted]: 'Accepted',
  [ChargebackStatus.Declined]: 'Declined',
  [ChargebackStatus.AutoAccepted]: 'Auto-accepted',
  [ChargebackStatus.Submitted]: 'Submitted',
  [ChargebackStatus.UnderReview]: 'Under review',
  [ChargebackStatus.MerchantAccepted]: 'Merchant accepted',
  [ChargebackStatus.MerchantDeclined]: 'Merchant declined',
  [ChargebackStatus.Resolved_Refunded]: 'Resolved (refunded)',
  [ChargebackStatus.Resolved_Declined]: 'Resolved (declined)',
  [ChargebackStatus.PayoutProcessing]: 'Payout processing',
  [ChargebackStatus.Released]: 'Released',
};

/** True when `value` is a known {@link ChargebackStatus} (string or API int code). */
export function isChargebackStatus(value: unknown): value is ChargebackStatus {
  return parseChargebackStatus(value) !== null;
}

/**
 * Parses API values into {@link ChargebackStatus}: integer codes, numeric strings,
 * or enum name strings. Trims whitespace; optional case-insensitive name match.
 */
export function parseChargebackStatus(
  raw: unknown,
  options?: { caseInsensitive?: boolean },
): ChargebackStatus | null {
  if (raw === null || raw === undefined) {
    return null;
  }

  const fromInt = chargebackStatusFromNumeric(raw);
  if (fromInt != null) {
    return fromInt;
  }

  const s = String(raw).trim();
  if (!s) {
    return null;
  }

  if (/^-?\d+$/.test(s)) {
    return chargebackStatusFromNumeric(Number(s));
  }

  if (STATUS_VALUES.has(s)) {
    return s as ChargebackStatus;
  }
  if (options?.caseInsensitive) {
    const lower = s.toLowerCase();
    for (const v of STATUS_VALUES) {
      if (v.toLowerCase() === lower) {
        return v as ChargebackStatus;
      }
    }
  }
  return null;
}

/** Human-readable label for a known status; falls back to the raw enum value. */
export function chargebackStatusDisplay(
  status: ChargebackStatus,
): string {
  return CHARGEBACK_STATUS_LABELS[status];
}

const MERCHANT_ACTIONABLE_STATUSES = new Set<ChargebackStatus>([
  ChargebackStatus.Untreated,
  ChargebackStatus.Submitted,
]);

/** True when the merchant may still accept or decline the case in the product UI. */
export function isMerchantChargebackActionable(
  status: ChargebackStatus,
): boolean {
  return MERCHANT_ACTIONABLE_STATUSES.has(status);
}

const OFFICER_ACTIONABLE_STATUSES = new Set<ChargebackStatus>([
  ChargebackStatus.Submitted,
  ChargebackStatus.UnderReview,
  ChargebackStatus.MerchantAccepted,
  ChargebackStatus.MerchantDeclined,
]);

/** True when an officer may approve or decline the case (pending officer decision). */
export function isOfficerChargebackActionable(
  status: ChargebackStatus,
): boolean {
  return OFFICER_ACTIONABLE_STATUSES.has(status);
}

/** Uses {@link parseChargebackStatusFromRecord}; false when status cannot be parsed. */
export function isOfficerChargebackActionableFromRecord(
  record: Record<string, unknown>,
): boolean {
  const parsed = parseChargebackStatusFromRecord(record);
  if (parsed == null) {
    return false;
  }
  return isOfficerChargebackActionable(parsed);
}

/**
 * Try common API field names on a chargeback row/detail payload.
 */
export function parseChargebackStatusFromRecord(
  record: Record<string, unknown>,
): ChargebackStatus | null {
  const keys = [
    'chargebackStatus',
    'status',
    'caseStatus',
    'chargeBackStatus',
    'reviewStatus',
  ] as const;
  for (const k of keys) {
    const parsed = parseChargebackStatus(record[k], { caseInsensitive: true });
    if (parsed != null) {
      return parsed;
    }
  }
  return null;
}

/**
 * Maps API chargeback origin codes to UI labels.
 * `0` — customer-raised; `1` — officer-raised. Numeric strings are accepted.
 */
export function parseChargebackOrigin(raw: unknown): string | null {
  if (raw === null || raw === undefined) {
    return null;
  }
  let code: number | null = null;
  if (typeof raw === 'number' && Number.isInteger(raw)) {
    code = raw;
  } else {
    const s = String(raw).trim();
    if (s === '0' || s === '1') {
      code = Number(s);
    }
  }
  if (code === 0) {
    return 'Customer Raised';
  }
  if (code === 1) {
    return 'Officer Raised';
  }
  return null;
}

/** Badge / highlight tone for status chips. */
export type ChargebackStatusTone =
  | 'neutral'
  | 'info'
  | 'warning'
  | 'success'
  | 'danger';

export function chargebackStatusTone(
  status: ChargebackStatus,
): ChargebackStatusTone {
  switch (status) {
    case ChargebackStatus.Untreated:
    case ChargebackStatus.Submitted:
      return 'warning';
    case ChargebackStatus.UnderReview:
    case ChargebackStatus.MerchantAccepted:
    case ChargebackStatus.MerchantDeclined:
    case ChargebackStatus.PayoutProcessing:
      return 'info';
    case ChargebackStatus.Accepted:
    case ChargebackStatus.Resolved_Refunded:
    case ChargebackStatus.Released:
      return 'success';
    case ChargebackStatus.Declined:
    case ChargebackStatus.Resolved_Declined:
      return 'danger';
    case ChargebackStatus.AutoAccepted:
      return 'neutral';
    default:
      return 'neutral';
  }
}

/**
 * Short explanation when merchant actions are hidden or for read-only viewers.
 */
export function chargebackStatusMerchantBannerMessage(
  status: ChargebackStatus,
): string {
  switch (status) {
    case ChargebackStatus.UnderReview:
      return 'This case is under review by SecurePay. You will be notified when there is an update.';
    case ChargebackStatus.MerchantAccepted:
      return 'You accepted this chargeback. An officer is reviewing your response.';
    case ChargebackStatus.MerchantDeclined:
      return 'You declined this chargeback. An officer is reviewing your response.';
    case ChargebackStatus.Accepted:
      return 'You have accepted this chargeback.';
    case ChargebackStatus.Declined:
      return 'You have declined this chargeback.';
    case ChargebackStatus.AutoAccepted:
      return 'This case was auto-accepted after the response deadline passed.';
    case ChargebackStatus.Resolved_Refunded:
      return 'This case is closed. The customer was refunded.';
    case ChargebackStatus.Resolved_Declined:
      return 'This case is closed. The dispute was declined.';
    case ChargebackStatus.PayoutProcessing:
      return 'A refund is being processed for this case.';
    case ChargebackStatus.Released:
      return 'Held funds have been released to your account.';
    case ChargebackStatus.Untreated:
    case ChargebackStatus.Submitted:
      return '';
  }
}

/** True when the case is finished from a merchant / customer-money perspective. */
export function isChargebackTerminalForMerchant(status: ChargebackStatus): boolean {
  switch (status) {
    case ChargebackStatus.Resolved_Refunded:
    case ChargebackStatus.Resolved_Declined:
    case ChargebackStatus.Released:
      return true;
    default:
      return false;
  }
}
